import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import {
  LocalizationProvider,
  MobileDateTimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ptBR } from "@mui/x-date-pickers/locales";
import dayjs from "dayjs";
import React from "react";
import { IInstrument } from "../../clients/answeringClient";
import {
  CreateAssignmentModalController,
  CreateAssignmentModalControllerDelegate,
} from "./controller";
import { trackGtagEvent } from "../../utils/analytics";

interface State {
  isLoading: boolean;
  instruments: IInstrument[];
  selectedInstrument?: number;
  selectedDate?: Date;
  assignmentName: string;
}

interface Props {
  delegate: CreateAssignmentModalDelegate;
}

export interface CreateAssignmentModalDelegate {
  shouldCloseModal: () => void;
  applicationShouldLogout: () => void;
  genericError: () => void;
  didCreateAssignment: (
    assignmentId: number,
    payload: { name: string; instrumentId: number; dueDate: Date }
  ) => void;
}

class CreateAssignmentModal
  extends React.Component<Props, State>
  implements CreateAssignmentModalControllerDelegate
{
  controller: CreateAssignmentModalController;

  constructor(props: Props) {
    super(props);
    this.state = {
      isLoading: true,
      instruments: [],
      selectedInstrument: undefined,
      selectedDate: undefined,
      assignmentName: "",
    };
    this.controller = new CreateAssignmentModalController(this);
  }

  // delegate
  didGetInstruments(instruments: IInstrument[]) {
    this.setState({
      isLoading: false,
      instruments,
    });
  }

  didCreateAssignment(
    assignmentId: number,
    payload: { name: string; instrumentId: number; dueDate: Date }
  ) {
    const fbq = typeof window !== "undefined" ? window.fbq : undefined;
    if (
      typeof window !== "undefined" &&
      typeof document !== "undefined" &&
      document.cookie.includes("catvanteAnalyticsCookiesAccepted=true") &&
      fbq
    ) {
      fbq("trackCustom", "ApplicationCreated", {
        application_id: assignmentId ? String(assignmentId) : "",
        name: payload.name,
        instrument_id: payload.instrumentId,
        deadline: payload.dueDate.toISOString(),
      });
    }

    trackGtagEvent("application_created", {
      application_id: assignmentId ? String(assignmentId) : "",
      name: payload.name,
      instrument_id: payload.instrumentId,
      deadline: payload.dueDate.toISOString(),
    });

    this.props.delegate.didCreateAssignment(assignmentId, payload);
  }

  genericError() {
    this.props.delegate.genericError();
  }

  applicationShouldLogout() {
    this.props.delegate.applicationShouldLogout();
  }

  // internals

  handleChangeDueDate(date: any) {
    this.setState({
      selectedDate: date && date.$d,
    });
  }

  handleChangeSelected(event: any) {
    this.setState({
      selectedInstrument: event.target.value,
    });
  }

  handleChangeName(event: any) {
    this.setState({
      assignmentName: event.target.value,
    });
  }

  componentDidMount() {
    const token = localStorage.getItem("AccessToken")!;
    this.controller.getManagedInstruments(token);
  }

  didClickClose() {
    this.props.delegate.shouldCloseModal();
  }

  didClickCreateAssignment() {
    if (
      this.state.selectedInstrument === undefined ||
      this.state.selectedInstrument === null ||
      !this.state.selectedDate
    ) {
      return;
    }

    const token = localStorage.getItem("AccessToken")!;
    const instrumentId =
      this.state.instruments[this.state.selectedInstrument!].id;
    const date = this.state.selectedDate!;
    const name = this.state.assignmentName;
    this.controller.createAssignment(token, date, instrumentId, name);
  }

  renderLoading() {
    return (
      <Box
        alignItems="center"
        display="flex"
        flexDirection="column"
        marginTop={2}
      >
        <CircularProgress />
      </Box>
    );
  }

  renderForm() {
    return (
      <Box
        alignItems="center"
        display="flex"
        flexDirection="column"
        gap={2}
      >
        <TextField
          variant="outlined"
          required
          label="Nome da aplicação"
          value={this.state.assignmentName}
          sx={{
            width: "100%",
          }}
          onChange={(event) => this.handleChangeName(event)}
        />

        <Autocomplete
          id="instrument-autocomplete"
          options={this.state.instruments}
          getOptionLabel={(instrument: IInstrument) => instrument.title || ""}
          value={
            this.state.selectedInstrument !== undefined &&
            this.state.selectedInstrument >= 0 &&
            this.state.selectedInstrument < this.state.instruments.length
              ? this.state.instruments[this.state.selectedInstrument]
              : null
          }
          onChange={(event, newValue: IInstrument | null) => {
            if (newValue) {
              const selectedIndex = this.state.instruments.indexOf(newValue);
              this.handleChangeSelected({ target: { value: selectedIndex } });
            } else {
              this.handleChangeSelected({ target: { value: undefined } });
            }
          }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Instrumento"
              variant="outlined"
              required
            />
          )}
          ListboxProps={{
            style: {
              maxHeight: 200,
            },
          }}
          noOptionsText="Nenhum instrumento encontrado"
          sx={{
            width: "100%",
          }}
        />

        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          localeText={
            ptBR.components.MuiLocalizationProvider.defaultProps.localeText
          }
        >
          <MobileDateTimePicker
            label="Data limite para conclusão da aplicação"
            format="D/M/YYYY HH:mm"
            ampm={false}
            value={
              this.state.selectedDate ? dayjs(this.state.selectedDate) : null
            }
            referenceDate={dayjs().hour(23).minute(59)}
            onAccept={(date) => {
              this.handleChangeDueDate(date);
            }}
            sx={{
              width: "100%",
            }}
            slotProps={{
              textField: {
                variant: "outlined",
                required: true,
              },
            }}
          />
        </LocalizationProvider>
      </Box>
    );
  }

  renderActions() {
    return (
      <>
        <Button
          variant="outlined"
          onClick={() => this.didClickClose()}
          color="error"
        >
          Cancelar
        </Button>

        <Button
          variant="contained"
          onClick={() => this.didClickCreateAssignment()}
          disabled={
            !this.state.selectedDate ||
            this.state.selectedInstrument === undefined ||
            this.state.assignmentName.length === 0
          }
        >
          Criar aplicação
        </Button>
      </>
    );
  }

  // Internals
  render() {
    return (
      <Dialog
        open
        onClose={() => this.didClickClose()}
        variant="modal"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle variant="modal">
          <AddIcon/>
          Criar aplicação
          </DialogTitle>
        <DialogContent variant="modal">
          {this.state.isLoading ? this.renderLoading() : this.renderForm()}
        </DialogContent>
        {!this.state.isLoading && (
          <DialogActions variant="modal" sx={{ gap: 1, flexWrap: "wrap" }}>
            {this.renderActions()}
          </DialogActions>
        )}
      </Dialog>
    );
  }
}

export default CreateAssignmentModal;
